'use client';

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { removeChatSessionsAction } from '../../server/action/chatActions';
import PopupTemplate from '@/app/ui/popupTemplate';
import { useTranslations } from 'next-intl';

interface ResetChatConfirmPopupProps {
  isOpen: boolean;
  onClose: () => void;
  avatarId: string;
  onSuccess?: () => void; // Optional callback for successful reset
}

export function ResetChatConfirmPopup({ isOpen, onClose, avatarId, onSuccess }: ResetChatConfirmPopupProps) {
  const t = useTranslations('chat');
  const [isResetting, setIsResetting] = useState(false);

  const handleConfirmReset = async () => {
    if (!avatarId) {
      console.log('No avatar ID provided');
      return;
    }

    console.log('Starting reset for avatar:', avatarId);
    setIsResetting(true);
    
    try {
      // Clear chat history from database
      console.log('Calling removeChatSessionsAction...');
      const result = await removeChatSessionsAction(avatarId);
      console.log('removeChatSessionsAction result:', result);
      
      if (result.success) {
        console.log('Chat reset successfully');
        // Close the popup first
        onClose();
        // Call success callback if provided (to close parent panel)
        if (onSuccess) {
          onSuccess();
        }
        // Trigger page refresh after a short delay to ensure popup closes
        setTimeout(() => {
          console.log('Reloading page...');
          window.location.reload();
        }, 100);
      } else {
        console.error('Failed to reset chat:', result.message);
        setIsResetting(false);
      }
    } catch (error) {
      console.error('Error resetting chat:', error);
      setIsResetting(false);
    }
  };

  if (!isOpen || typeof window === 'undefined') return null;

  return createPortal(
    <PopupTemplate
      isOpen={isOpen}
      onClose={onClose}
      closeable={true}
      isTransparent={false}
    >
      <div className="text-center p-8">
        <div className="flex justify-center mb-4">
          <AlertTriangle className="h-12 w-12 text-orange-500" />
        </div>
        <h2 className="text-white text-xl font-semibold mb-2 font-['Montserrat',Helvetica]">
          {t('resetChatHistory')}
        </h2>
        <p className="text-gray-400 text-sm mb-6 font-['Montserrat',Helvetica]">
          {t('resetChatWarning')}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 text-white hover:bg-[#2a2a2e] rounded-xl transition-colors font-['Montserrat',Helvetica]"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleConfirmReset}
            disabled={isResetting}
            className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-['Montserrat',Helvetica]"
          >
            {isResetting ? t('resetting') : t('resetChat')}
          </button>
        </div>
      </div>
    </PopupTemplate>,
    document.body
  );
}

interface BacktrackConfirmPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function BacktrackConfirmPopup({ isOpen, onClose, onConfirm }: BacktrackConfirmPopupProps) {
  const t = useTranslations('chat');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error during backtrack:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen || typeof window === 'undefined') return null;

  return createPortal(
    <PopupTemplate
      isOpen={isOpen}
      onClose={onClose}
      closeable={true}
      isTransparent={false}
    >
      <div className="text-center p-8">
        <div className="flex justify-center mb-4">
          <RotateCcw className="h-12 w-12 text-white" />
        </div>
        <h2 className="text-white text-xl font-semibold mb-2 font-['Montserrat',Helvetica]">
          {t('backtrackToMessage')}
        </h2>
        <p className="text-gray-400 text-sm mb-6 font-['Montserrat',Helvetica]">
          {t('backtrackWarning')}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 text-white hover:bg-[#2a2a2e] rounded-xl transition-colors font-['Montserrat',Helvetica]"
          >
            {t('cancel')}
          </button>
           <button
             onClick={handleConfirm}
             disabled={isProcessing}
             className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-['Montserrat',Helvetica]"
           >
            {isProcessing ? t('reverting') : t('backtrack')}
          </button>
        </div>
      </div>
    </PopupTemplate>,
    document.body
  );
}